import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogBody, DialogHeader, DialogFooter } from "@material-tailwind/react";
import { TestableButton, TestableText, TestableTextInput, TestableSelect, TestableSelectItem } from "@components/general/TestableComponents";
import { Textarea } from "@tremor/react";
import { useFormik } from "formik";
import * as yup from "yup";

import useEvent from "@datahooks/events/useEvent";
import useEventTypes from "@datahooks/events/useEventTypes";
import useEventManager from "@datahooks/events/useEventManager";
import useEventLinkManager from "@datahooks/events/useEventLinkManager";

import { useProjectContext } from "@datahooks/ProjectContext";
import { useReleaseContext } from "@datahooks/ReleaseContext";

import { convertInputToTimezoneAwareISO, convertDateToLocal } from "@datahooks/metadata/DataUtils";
import { validateUrl } from "@disqo/shared";

import AlertManager from "@components/general/AlertManager";
import DeleteEventDialog from "./DeleteEventDialog";

import PropTypes from "prop-types";

// Yup Validation schema
const testEventSchema = yup.object().shape({
  event_type_id: yup.string().required("Please select an event type"),
  start_time: yup.date().nullable(),
  end_time: yup.date().nullable(),
  severity: yup.string().required("Please select a severity"),
  event_description: yup.string().max(1000, "Please enter less than 1,000 characters"),
  external_link: yup.string().test(
    'is-valid-url',
    'Please enter a valid URL',
    function (value) {
      console.log('Validating URL1:', value);
      if (!value) return true;
      return validateUrl(value);
    }),
});

const projectEventSchema = yup.object().shape({
  event_type_id: yup.string().required("Please select an event type"),
  start_time: yup.date().required("Please set a start time"),
  end_time: yup
    .date()
    .nullable()
    .test(
      "end_time-after-start_time",
      "End time cannot be before start time",
      function (value) {
        const { start_time } = this.parent;
        if (!start_time || !value) return true;
        const startDate = new Date(start_time);
        const endDate = new Date(value);
        if (Number.isNaN(startDate) || Number.isNaN(endDate)) return true;
        return endDate >= startDate;
      }
    ),
  severity: yup.string().required("Please select a severity"),
  event_description: yup.string().max(1000, "Please enter less than 1,000 characters"),
  external_link: yup.string().test(
    'is-valid-url',
    'Please enter a valid URL',
    function (value) {
      console.log('Validating URL2:', value);
      if (!value) return true;
      return validateUrl(value);
    }),
});

const EventEditForm = ({ isOpen, onClose, onSave, eventId, eventLevel = "Project", testName, testTypeId, additionalId, testLinkId, initialFormValues }) => {
  const isEdit = Boolean(eventId);
  const [ confirmDelete, setConfirmDelete ] = useState(false);
  const [ errorMessage, setErrorMessage ] = useState(null);
  const [ isInitialised, setIsInitialised] = useState(false);

  const { projectId } = useProjectContext();
  const { release } = useReleaseContext();

  // Load event details when editing and the dialog is open
  const { event: eventData, isLoading: eventLoading, error: eventError, refetch } = useEvent(eventId, {
    enabled: isOpen && isEdit,
  });

  // Load available event types for the given event level when the dialog is open
  const { eventTypes, isLoading: eventTypesLoading, error: eventTypesError } = useEventTypes(eventLevel, {
    enabled: isOpen,
  });

  //After a successful save
  const eventUpdated = (result) => {
      if (confirmDelete) {
        setConfirmDelete(false);
      }
      
      if (!isEdit) {
          formik.resetForm();
      }

      onClose();

      (onSave && typeof onSave === 'function' && onSave()); // Call the callback function if provided
  }

  //After a failed save
  const eventSaveFailed = (error) => {
      setErrorMessage(`Failed to save event: ${error?.errorResponse?.message || 'Unknown error'}`);
  }

  //Hook for performing the delete action
  const { performAction } = useEventManager(eventUpdated, eventSaveFailed);

  //Hook for performing the detach action
  const { performAction: performLinkAction } = useEventLinkManager(eventUpdated, eventSaveFailed);

  //Default values
  const initialValues = useMemo(() => {
    if (isEdit && eventData) {
      return {
        event_type_id: eventData.event_type_id ? String(eventData.event_type_id) : "",
        event_description: eventData.event_description || "",
        start_time: eventData.start_time ? convertDateToLocal(eventData.start_time, false, true) : "",
        end_time: eventData.end_time ? convertDateToLocal(eventData.end_time, false, true) : "",
        severity: eventData.severity || "low",
        external_link: eventData.external_link || "",
      };
    }
    const baseValues = {
      event_type_id: "",
      event_description: "",
      start_time: "",
      end_time: "",
      severity: "low",
      external_link: "",
    };

    if (initialFormValues && typeof initialFormValues === 'object') {
      return {
        ...baseValues,
        ...initialFormValues,
      };
    }

    return baseValues;
  }, [isEdit, eventData, initialFormValues]);

  const validationSchema = useMemo(() => {
    return eventLevel === "Test" ? testEventSchema : projectEventSchema;
  }, [eventLevel]);
  
  const formik = useFormik({
    initialValues,
    validationSchema: validationSchema,
    enableReinitialize: true, // Reinitialise when initialValues change
    onSubmit: (values) => {
      const eventObj = {
        ...(isEdit && { event_id: eventId }),
        ...values,
      };
      doSave(eventObj);
    },
  });  

  useEffect(() => {
    if (!isEdit && isOpen && !isInitialised) {
      formik.resetForm();
      setIsInitialised(true);
    }
  }, [isOpen, isEdit, isInitialised, formik]);
  
  // Reset flag when dialog closes so that subsequent opens work correctly
  useEffect(() => {
    if (!isOpen) {
      setIsInitialised(false);
    }
  }, [isOpen]);

  // When event type changes in add mode, update default severity.
  useEffect(() => {
    if (!isEdit && eventTypes && formik.values.event_type_id) {
      const selectedType = eventTypes.find((et) => String(et.id) === formik.values.event_type_id);
      if (selectedType?.default_severity) {
        formik.setFieldValue("severity", selectedType.default_severity);
      }
    }
  }, [formik.values.event_type_id, eventTypes, isEdit]);  

  //Always refresh the data when the dialog is opened for editing
  useEffect(() => {
    if (isOpen && eventId) {
      refetch();
    }
  }, [isOpen, eventId, refetch]);
  
  // Save the event data
  const doSave = (eventObj) => {
    if (isEdit) {
      if (eventObj.start_time) {
        eventObj.start_time = convertInputToTimezoneAwareISO(eventObj.start_time);
      }
      if (eventObj.end_time) {
        eventObj.end_time = convertInputToTimezoneAwareISO(eventObj.end_time);
      }
    } else {
      if (eventLevel === 'Test') {
        // Set all IDs on insert
        eventObj.release_id = release.release_id;
        eventObj.test_type_id = testTypeId;
        eventObj.test_name = testName;
        eventObj.additional_id = additionalId;
        eventObj.start_time = release.creation_date;
      }

      if (eventObj.start_time) {
        eventObj.start_time = convertInputToTimezoneAwareISO(eventObj.start_time);
      }
      if (eventObj.end_time) {
        eventObj.end_time = convertInputToTimezoneAwareISO(eventObj.end_time);
      }

      eventObj.project_id = projectId;
    }

    // Map the form field 'event_description' to the API field 'description'
    if (eventObj.event_description !== undefined) {
      eventObj.description = eventObj.event_description;
      delete eventObj.event_description;
    }

    // Remove any blank fields before saving
    eventObj = Object.fromEntries(
      Object.entries(eventObj).filter(([_, value]) => value !== "")
    );
  
    performAction(eventObj, 'PUT');
  };  

  const handleDelete = async () => {
    await performAction({ event_id: eventId }, 'DELETE');
  };
  
  const handleDetach = async () => {
    if (!testLinkId) return;
    await performLinkAction({ link_id: testLinkId }, 'DELETE');
  };

  const handleExternalLinkBlur = (e) => {
    const value = e.target.value.trim();
    formik.setFieldValue("external_link", value);
    formik.handleBlur(e);
  };

  return (
    <>
      <Dialog data-testid="event-edit-form" open={isOpen} handler={onClose} dismiss={{outsidePress: false, escapeKey: false, bubbles: false}}>
        <form onSubmit={formik.handleSubmit}>
          <DialogHeader className="border-b border-gray-200">
              <TestableText dataTestId="eef-header-title" variant="h4" className="text-blue-500">{isEdit ? `${eventData.title}` : "Add New Event"}</TestableText>
              {isEdit && eventData.link_count > 1 && <TestableText dataTestId="eef-header-link-count" variant="h6" className="text-gray-500 ml-auto">{`Attached to ${eventData.link_count} tests`}</TestableText> }
            </DialogHeader>
          <DialogBody>
            {isEdit && eventLoading && <div>Loading event data...</div>}
            {eventError && <div>Error loading event data</div>}
            <div className="flex flex-col gap-4 mb-2" >
              {/* Event Type Select */}
              {eventTypesLoading && <div>Loading event types...</div>}
              {eventTypesError && <div>Error loading event types</div>}
              {(eventTypes) && (
                <>
                  <TestableText dataTestId="eef-type-label" variant="h5" className="text-blue-500">Type</TestableText>
                  <TestableSelect
                    dataTestId="eef-type-select"
                    label="Event Type"
                    value={formik.values.event_type_id}
                    onChange={(value) => formik.setFieldValue("event_type_id", value)}
                    error={formik.touched.event_type_id && Boolean(formik.errors.event_type_id)}
                  >
                    {eventTypes.map((et) => (
                      <TestableSelectItem dataTestId="eef-type-select-item" key={et.id} value={String(et.id)}>{et.title}</TestableSelectItem>
                    ))}
                  </TestableSelect>
                </>
              )}

              <TestableText dataTestId="eef-description-label" variant="h5" className="text-blue-500">Description</TestableText>
              <Textarea
                data-testid="eef-description-input"
                label="Description"
                name="event_description"
                value={formik.values.event_description}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.event_description && Boolean(formik.errors.event_description)}
                errorMessage={formik.errors.event_description}
                placeholder="Event description..."
                className="h-24"
              />

              {(eventLevel !== 'Test') && (
              <div className="flex flex-row gap-2 w-full">
                <div className="w-full">
                  <TestableText dataTestId="eef-start-time-label" variant="h5" className="text-blue-500">Start Time</TestableText>
                  <TestableTextInput
                    dataTestId="eef-start-time-input"
                    label="Start Time"
                    type="datetime-local"
                    name="start_time"
                    value={formik.values.start_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.start_time && Boolean(formik.errors.start_time)}
                    errorMessage={formik.errors.start_time}
                  />
                </div>

                <div className="w-full">
                  <TestableText dataTestId="eef-end-time-label" variant="h5" className="text-blue-500">End Time</TestableText>
                  <TestableTextInput
                    dataTestId="eef-end-time-input"
                    label="End Time"
                    type="datetime-local"
                    name="end_time"
                    value={formik.values.end_time}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.end_time && Boolean(formik.errors.end_time)}
                    errorMessage={formik.errors.end_time}
                  />
                </div>
              </div>
              )}

              <TestableText dataTestId="eef-severity-label" variant="h5" className="text-blue-500">Severity</TestableText>
              <TestableSelect
                dataTestId="eef-severity-select"
                label="Severity"
                value={formik.values.severity}
                onChange={(value) => formik.setFieldValue("severity", value)}
                error={formik.touched.severity && Boolean(formik.errors.severity)}
              >
                <TestableSelectItem dataTestId="eef-severity-select-item-low" value="low">Low</TestableSelectItem>
                <TestableSelectItem dataTestId="eef-severity-select-item-medium" value="medium">Medium</TestableSelectItem>
                <TestableSelectItem dataTestId="eef-severity-select-item-high" value="high">High</TestableSelectItem>
                <TestableSelectItem dataTestId="eef-severity-select-item-critical" value="critical">Critical</TestableSelectItem>
              </TestableSelect>

              <TestableText dataTestId="eef-external-link-label" variant="h5" className="text-blue-500">Link to more details</TestableText>
              <TestableTextInput
                dataTestId="eef-external-link-input"
                label="External Link"
                name="external_link"
                value={formik.values.external_link}
                onChange={formik.handleChange}
                onBlur={handleExternalLinkBlur}
                error={formik.touched.external_link && Boolean(formik.errors.external_link)}
                errorMessage={formik.errors.external_link}
                placeholder="Link to more details..."
              />
            </div>

            <AlertManager errorMessage={errorMessage} setErrorMessage={setErrorMessage} className="mt-5" />

          </DialogBody>
          <DialogFooter className="flex justify-between border-t border-gray-200 mt-5">
            {isEdit && (
              <TestableButton dataTestId="eef-delete-button" className="self-start" color="pink" onClick={() => setConfirmDelete(true)}>
                Delete
              </TestableButton>
            )}
            <div className="flex gap-1 ml-auto ">
              <TestableButton dataTestId="eef-cancel-button" variant="outlined" onClick={onClose}>
                Cancel
              </TestableButton>
              <TestableButton dataTestId="eef-submit-button" color="blue" type="submit" disabled={!formik.dirty}>
                {isEdit ? "Update" : "Add"}
              </TestableButton>
            </div>
          </DialogFooter>
        </form>
      </Dialog>

    {confirmDelete && ( 
      <DeleteEventDialog
        eventId={eventId}
        testLinkId={testLinkId}
        eventLevel={eventLevel}
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onDelete={handleDelete}
        onDetach={handleDetach}
      />
    )}
    </>
  );
};

EventEditForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  eventId: PropTypes.number, // For edit; null/undefined for add
  eventLevel: PropTypes.string.isRequired, // Used to load event types
  testName: PropTypes.string, // For test events only
  testTypeId: PropTypes.number, // For test events only
  additionalId: PropTypes.string, // For test events only
  testLinkId: PropTypes.number, // For test events only
  initialFormValues: PropTypes.shape({
    event_type_id: PropTypes.string,
    event_description: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    severity: PropTypes.string,
    external_link: PropTypes.string,
  }),
};

export default EventEditForm;